import React, { useMemo, useState } from 'react';
import backgroundImage from 'assets/images/payments-terminal-image.png';
import { Card, IconText } from 'shared/components';
import { Box, Collapse } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useStripe } from 'shared/hooks';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  integrationSystemDisplayName,
  useBitwerxData,
  useIntegrationsProvider,
} from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsPaymentsEvents } from 'shared/providers/TrackingProvider/EventNames';

export const PaymentOptInBanner = (): JSX.Element => {
  const { hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const { needsBitwerxForPaymentWritebacks, bitwerxPending, hasBitwerx, writebackCapable, invoiceReadCapable } =
    useBitwerxData();
  const { account, login, loading } = useStripe();
  const [warnAdminOnly, setWarnAdminOnly] = useState(false);
  const { trackEvent } = useTracking();

  const handleConnectStripe = (): void => {
    if (hasAdminRoleAtCurrentClinic) {
      trackEvent(SettingsPaymentsEvents.StripeAccountCreation);
      login();
    } else {
      setWarnAdminOnly(true);
    }
  };

  const handleManageStripeSettings = (): void => {
    trackEvent(SettingsPaymentsEvents.StripeAccountUpdates);
    login();
  };

  const showWritebackCopy = useMemo(
    () => (needsBitwerxForPaymentWritebacks || bitwerxPending || hasBitwerx) && writebackCapable,
    [needsBitwerxForPaymentWritebacks, bitwerxPending, hasBitwerx, writebackCapable],
  );

  const isClinicEligibleForPayments = true; // TODO: Replace with actual eligibility check after we have it defined again

  return (
    <Card backgroundImage={backgroundImage} backgroundPosition="top right" w={552} mb={5}>
      <Heading size="md" w="320px">
        Simplify your workflow by accepting payments with Otto
      </Heading>
      <Box my={5} w="320px">
        <IconText name="clock">Setting up payments is fast and easy</IconText>
        <IconText name="chatBubble" align="top">
          Request payments in Conversations
        </IconText>
        <IconText name="robot" align="top">
          Set up automated payment requests through Automations
        </IconText>
        {invoiceReadCapable && (
          <IconText name="doubleArrowCircle" align="top">
            {`Sync data from ${primaryIntegrationName} to see ${
              primaryIntegrationName === integrationSystemDisplayName.AVIMARK ? 'balances' : 'invoices'
            } in Flow ${showWritebackCopy ? `and write back payments to ${primaryIntegrationName}` : ''}`}
          </IconText>
        )}
      </Box>
      {isClinicEligibleForPayments ? (
        <>
          {!account ? (
            <>
              <Button
                iconName="externalLink"
                onClick={handleConnectStripe}
                isLoading={loading}
                loadingText="Connecting with Stripe"
              >
                Connect with Stripe
              </Button>
              <Collapse in={warnAdminOnly} animateOpacity>
                <Text size="sm" as="div" mt={2} variant="danger">
                  You must be an admin to setup payments
                </Text>
              </Collapse>
            </>
          ) : (
            <>
              <Alert
                status="info"
                title="Stripe needs more information from you in order to complete setup."
                description="If you have provided all required information, please wait a few minutes and refresh this page."
                hideCloseButton
                mt={2}
                mb={4}
                w="320px"
              />
              <Button
                iconName="externalLink"
                onClick={handleManageStripeSettings}
                isLoading={loading}
                loadingText="Connecting with Stripe"
              >
                Manage Stripe Settings
              </Button>
            </>
          )}
        </>
      ) : (
        <Alert
          status="info"
          title="Onboarding not completed"
          description="Contact your Customer Success Representative to complete onboarding and begin processing payments."
          hideCloseButton
          mt={2}
          w="320px"
        />
      )}
    </Card>
  );
};
